import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import About from '@/pages/home/components/About';
import WhyChooseUs from '@/pages/home/components/WhyChooseUs';
import GlobalPresence from '@/pages/home/components/GlobalPresence';
import Stats from '@/pages/home/components/Stats';
import OurStory from './components/OurStory';
import VisionMissionValues from './components/VisionMissionValues';

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://thoughtic.com/#organization",
  "name": "Thoughtic Solutions",
  "url": "https://thoughtic.com",
  "logo": "https://thoughtic.com/thoughtic-solutions.png",
  "image": "https://thoughtic.com/thoughtic-solutions.png",
  "description": "Bridge your tech skill gaps with flexible, plug-and-play developers. Thoughtic Solutions provides vetted remote engineering talent for IT staff augmentation, dedicated teams, contract hiring, and offshore development since 2016.",
  "foundingDate": "2016",
  "founders": [
    {
      "@type": "Person",
      "name": "Thoughtic Leadership Team"
    }
  ],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IN",
    "addressRegion": "India"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "20.5937",
    "longitude": "78.9629"
  },
  "telephone": "+91XXXXXXXXX",
  "email": "info@thoughtic.com",
  "sameAs": [
    "https://linkedin.com/company/thoughtic",
    "https://twitter.com/thoughtic",
    "https://facebook.com/thoughtic",
    "https://youtube.com/@thoughtic"
  ],
  "areaServed": [
    {
      "@type": "Country",
      "name": "United States"
    },
    {
      "@type": "Country",
      "name": "United Kingdom"
    },
    {
      "@type": "Country",
      "name": "Germany"
    },
    {
      "@type": "Country",
      "name": "Canada"
    },
    {
      "@type": "Country",
      "name": "Australia"
    },
    {
      "@type": "Country",
      "name": "India"
    }
  ],
  "priceRange": "$$",
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ]
};

function PageHero({ title, subtitle }: { title: string; subtitle: string }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-12 md:pt-32 md:pb-16 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
            <i className="ri-building-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Our Company</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4">
            {title}
          </h1>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  useEffect(() => {
    const scriptId = 'localbusiness-schema-ld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(localBusinessSchema);

    // AboutPage schema
    const aboutScriptId = 'aboutpage-schema-ld';
    let aboutScript = document.getElementById(aboutScriptId) as HTMLScriptElement | null;
    if (!aboutScript) {
      aboutScript = document.createElement('script');
      aboutScript.id = aboutScriptId;
      aboutScript.type = 'application/ld+json';
      document.head.appendChild(aboutScript);
    }
    aboutScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Thoughtic Solutions",
      "url": "https://thoughtic.com/about",
      "description": "Thoughtic Solutions bridges the gap between visionary companies and world-class engineering talent. Pre-vetted remote developers, dedicated teams, and end-to-end software development since 2016.",
      "about": {
        "@type": "Organization",
        "name": "Thoughtic Solutions",
        "foundingDate": "2016",
        "description": "Custom software development and IT staffing company providing pre-vetted remote engineering talent for startups, SMEs, and enterprises worldwide.",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN"
        }
      }
    });

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
      const abExisting = document.getElementById(aboutScriptId);
      if (abExisting) abExisting.remove();
    };
  }, []);

  return (
    <PageLayout title="About Thoughtic Solutions" description="Learn about Thoughtic Solutions — our mission, values, and how we have helped 200+ companies hire vetted remote developers since 2016. Based in India, serving global clients." keywords="about Thoughtic Solutions, remote engineering team company, IT staffing company India, software development agency, offshore developer recruitment, engineering talent partner">
      <PageHero
        title="About Thoughtic Solutions"
        subtitle="Bridging the gap between visionary companies and world-class engineering talent since 2016."
      />
      <OurStory />
      <About />
      <VisionMissionValues />
      <WhyChooseUs />
      <GlobalPresence />
      <Stats />
    </PageLayout>
  );
}